import { select, call, put, takeEvery } from 'redux-saga/effects';
import { approvalsApi } from '@/domain/approvals/api';
import { showApiErrorToast } from '@/services/api/errorToast';

import * as DocumentActions from '@/domain/documents/documentsActions';
// utils
import { documentAdapter, documentAdapterValidated } from '@/domain/documents/helpers';
import { documentDocumentIdSelector } from '@/domain/documents/documentSelector';
// adapters
import { ApprovalsStoreAdapter } from '@/domain/approvals/helpers';
import * as ApprovalActions from '@/domain/approvals/actions';
import { decideNavigation } from '@/domain/approvals/navigation/decideNavigation';
import { getNavigationDecisionInput } from '@/domain/approvals/navigation/getNavigationDecisionInput';
import { applyPostActionNavigation } from '@/domain/approvals/navigation/applyPostActionNavigation';
import { yieldToastForPostAction } from '@/domain/approvals/navigation/yieldToastForPostAction';
import { forceErpSyncAction } from '@/domain/journal/actions';
import { isMobile } from '@/lib/systemHelpers/browserHelpers';

export function* approveDocumentApprovals({ payload }) {
  const { resolve, reject, requestErpSync = false, note } = payload;
  const documentID = yield select(documentDocumentIdSelector);

  try {
    const data = yield call(approvalsApi.approveDocument, {
      body: {
        documentID,
        ...(note ? { note } : {}),
      },
    });

    yield put({
      type: DocumentActions.documentUpdateInListByID.type,
      payload: documentAdapter(data),
    });

    yield put({
      type: DocumentActions.documentGet.success,
      payload: documentAdapterValidated(data),
    });

    const decisionInput = yield* getNavigationDecisionInput();
    const postAction = decideNavigation({ action: 'approve', ...decisionInput });

    yield* yieldToastForPostAction(postAction, 'success', {
      id: 'document.approval.approve.success',
      defaultMessage: 'Document was approved for payment',
    });

    if (requestErpSync && postAction === 'stay' && !isMobile) {
      yield put({
        type: forceErpSyncAction.type,
        payload: { documentID, reindexCache: false },
      });
    }

    if (typeof resolve === 'function') resolve();

    yield* applyPostActionNavigation(postAction);
  } catch (error) {
    showApiErrorToast(error, {
      id: 'document.approval.approve.error',
      defaultMessage: 'Document was not approved for payment',
    });
    if (typeof reject === 'function') reject();
  }
}

export function* rejectApproval({ payload: { reason: reasonID, message, resolve, reject } }) {
  const documentID = yield select(documentDocumentIdSelector);
  try {
    const data = yield call(approvalsApi.rejectDocument, {
      body: {
        documentID,
        reasonID,
        message,
      },
    });

    yield put({
      type: DocumentActions.documentUpdateInListByID.type,
      payload: documentAdapter(data),
    });

    // updates signPosition after rejection
    yield put({
      type: DocumentActions.documentGet.success,
      payload: documentAdapterValidated(data),
    });

    yield put({
      type: ApprovalActions.rejectApprovalAction.success,
      payload: ApprovalsStoreAdapter(data.approvals),
    });

    const decisionInput = yield* getNavigationDecisionInput();
    const postAction = decideNavigation({ action: 'reject', ...decisionInput });

    yield* yieldToastForPostAction(postAction, 'error', {
      id: 'document.rejectAlert.approvalWasRejected',
      defaultMessage: 'Approval was rejected',
    });

    if (typeof resolve === 'function') resolve();

    yield* applyPostActionNavigation(postAction);
  } catch (err) {
    showApiErrorToast(err, { id: 'document.approval.reject.error', defaultMessage: 'Approval was not rejected' });
    if (typeof reject === 'function') reject();
  }
}

export default function* approval() {
  yield takeEvery(ApprovalActions.approveApprovalAction.type, approveDocumentApprovals);
  yield takeEvery(ApprovalActions.rejectApprovalAction.type, rejectApproval);
}
